// Shared blog types and pure, client-safe helpers.
//
// Article DATA lives in the database (authored in the Jelloducks CMS at /cms-admin).
// The DB-backed fetchers are in `blog-queries.ts` (server-only). This file must
// stay free of server-only imports (no Prisma) because client components import
// the helpers below.

export type BlogLanguage = "th" | "en";

/** The person an article is credited to. */
export type BlogAuthor = {
  name: string;
  /** Role or credential, e.g. "ที่ปรึกษาระบบร้านอาหาร" — may be empty. */
  title: string;
};

export type BlogPostEnglish = {
  title: string;
  excerpt: string;
  description: string;
  /** Rich-text body as HTML. */
  content: string;
  seoTitle: string;
  seoDescription: string;
  authorName: string;
  authorTitle: string;
};

export type BlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  description: string;
  /** Rich-text body as HTML (produced by the CMS Tiptap editor). */
  content: string;
  publishedAt: string; // YYYY-MM-DD
  /** Last save in the CMS, as a full ISO timestamp — `dateModified` / lastmod. */
  updatedAt: string;
  image: string;
  tags: string[];
  category?: string;
  /** `<title>` override from the CMS; empty means use `title`. */
  seoTitle: string;
  /** Meta description override; empty means use `description`. */
  seoDescription: string;
  /** Null when the CMS names nobody — the byline then falls back to the brand. */
  author: BlogAuthor | null;
  /** English translation — present only when the CMS has one. */
  en?: BlogPostEnglish | null;
};

/** Strip HTML tags to test whether rich-text content has any real text. */
function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.replace(/<[^>]*>/g, "").trim().length > 0);
}

/**
 * Whether this post has any usable English content. A post counts as having an
 * English version when at least one of its EN fields (title, excerpt, body) is
 * filled — we don't require all of them. The SEO and byline fields don't count:
 * they describe a translation, they aren't one.
 */
export function hasEnglish(post: BlogPost): boolean {
  const en = post.en;
  if (!en) return false;
  return hasText(en.title) || hasText(en.excerpt) || hasText(en.content);
}

/**
 * Returns the post in the requested language. For English we merge the EN
 * overrides field-by-field: each field uses its English value when present and
 * falls back to the original Thai when that EN field is empty. A post with no
 * English content at all stays fully Thai.
 *
 * The SEO overrides are the exception. They fall back to the *English* title
 * and description (by coming back empty), never to the Thai override — a Thai
 * `<title>` on an English page is worse than no override at all.
 */
export function getLocalizedBlogPost(
  post: BlogPost,
  language: BlogLanguage
): BlogPost {
  if (language !== "en" || !post.en) return post;
  const en = post.en;
  const pick = (english: string, thai: string) =>
    hasText(english) ? english : thai;
  const authorName = pick(en.authorName, post.author?.name ?? "");

  return {
    ...post,
    title: pick(en.title, post.title),
    excerpt: pick(en.excerpt, post.excerpt),
    description: pick(en.description, post.description),
    content: pick(en.content, post.content),
    seoTitle: en.seoTitle.trim(),
    seoDescription: en.seoDescription.trim(),
    author: authorName
      ? { name: authorName, title: pick(en.authorTitle, post.author?.title ?? "") }
      : null,
  };
}

/** The `<title>` / og:title text — the CMS override when there is one. */
export function seoTitleOf(post: BlogPost) {
  return post.seoTitle || post.title;
}

/** The meta description — the CMS override when there is one, else the excerpt. */
export function seoDescriptionOf(post: BlogPost) {
  return post.seoDescription || post.description;
}

/**
 * Returns the posts to display in the given language. Every published post is
 * always shown: posts with an English translation render in English, and posts
 * without one fall back to their original Thai (via `getLocalizedBlogPost`).
 * We intentionally do NOT hide untranslated posts in English mode.
 */
export function filterPostsByLanguage(
  posts: BlogPost[],
  _language: BlogLanguage
): BlogPost[] {
  return posts;
}

/**
 * A calendar date for display. Formatted in UTC because every date here is a
 * UTC calendar day (`YYYY-MM-DD`): formatting it in the runtime's zone gave the
 * server (UTC) and a browser west of Greenwich two different days, and a
 * hydration mismatch between them.
 */
export function formatBlogDate(date: string, language: BlogLanguage = "th") {
  return new Intl.DateTimeFormat(language === "en" ? "en-US" : "th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(date));
}

export function formatThaiDate(date: string) {
  return formatBlogDate(date, "th");
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  "#39": "'",
  nbsp: " ",
};

/** Rich-text HTML as one line of plain text. */
export function plainText(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, entity: string) => ENTITIES[entity])
    .replace(/\s+/g, " ")
    .trim();
}

function escapeAttribute(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * v1 addresses that articles written before the v2 rebuild still link to, and
 * where each one's content lives now. They all redirect (next.config.ts), but a
 * link that has to hop through a 301 passes less than one that doesn't — and
 * the old targets redirected to sections of the home page, never reaching the
 * product pages that now exist.
 */
const MOVED_LINKS: Record<string, string> = {
  "/crm": "/product/crm",
  "/pos": "/product/pos",
  "/pricing": "/#pricing",
  // Every link to it sits in a customer-retention article, so its successor
  // is the CRM page rather than POS.
  "/solutions/restaurant": "/product/crm",
};

/** An image `alt` that is really just the uploaded file's name. */
const IMAGE_FILENAME = /^[^\s/]+\.(?:jpe?g|png|webp|gif|avif|svg)$/i;

/**
 * An article body as the public page should serve it.
 *
 * The CMS HTML is left untouched in the database; these corrections are made
 * on the way out, so they apply to every article already written and to any
 * written the same way later:
 *
 * - Links to v1 addresses point straight at their v2 successors (see
 *   `MOVED_LINKS`), and links to other articles keep the reader's language.
 * - `<h1>` becomes `<h2>`. The page's one `<h1>` is the article title; the
 *   editor also offers H1, and the English FAQ boxes use it.
 * - An image whose `alt` is empty or a file name (the editor used to fill in
 *   `12075.jpg`) gets the article title and its position instead. A file name
 *   tells a search engine, an answer engine and a screen reader nothing; the
 *   title at least says what the picture is about. Real descriptions are now
 *   written in the editor, and those are kept as they are.
 */
export function rewriteArticleHtml(
  html: string,
  language: BlogLanguage,
  title: string
): string {
  let imageIndex = 0;

  return html
    .replace(
      /href="(?:https?:\/\/(?:www\.)?jelloducks\.com)?(\/[^"#?]*)([#?][^"]*)?"/g,
      (match, path: string, rest: string | undefined) => {
        const clean = path.replace(/\/+$/, "") || "/";
        const moved = MOVED_LINKS[clean];
        if (moved) return `href="${moved}"`;
        if (language === "en" && /^\/blog(?:\/|$)/.test(clean)) {
          return `href="/en${clean}${rest ?? ""}"`;
        }
        return match;
      }
    )
    .replace(/<(\/?)h1\b/gi, "<$1h2")
    .replace(/<img\b([^>]*)>/gi, (tag, attrs: string) => {
      imageIndex += 1;
      const alt = attrs.match(/\salt="([^"]*)"/i);
      const current = alt?.[1].trim() ?? "";
      if (current && !IMAGE_FILENAME.test(current)) return tag;

      const fallback = `alt="${escapeAttribute(`${title} (${imageIndex})`)}"`;
      return alt
        ? tag.replace(alt[0], ` ${fallback}`)
        : tag.replace(/^<img\b/i, `<img ${fallback}`);
    });
}

export type FaqItem = { question: string; answer: string };

const FAQ_HEADING = /คำถามที่พบบ่อย|\bFAQ\b|frequently asked/i;
const QUESTION = /^(?:Q|ถาม)\s*[:：.]\s*(.+)$/i;

/**
 * The question-and-answer pairs from an article's FAQ section, for FAQPage
 * structured data.
 *
 * Read from the body rather than typed into a separate field, so the markup can
 * never disagree with what the page shows — Google's one hard rule for FAQ
 * markup. The articles write the section the same way in both of the shapes
 * the editor produces:
 *
 *   heading containing "คำถามที่พบบ่อย" / "FAQ"
 *   "Q: …" as a bold paragraph or an <h3>, then one or more answer paragraphs
 *   … until an <hr> (or the end of the article)
 *
 * Anything that doesn't fit returns an empty list, and no FAQPage is emitted.
 */
export function extractFaq(html: string): FaqItem[] {
  const heading = [...html.matchAll(/<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi)].find(
    (match) => FAQ_HEADING.test(plainText(match[2]))
  );
  if (!heading || heading.index === undefined) return [];

  let section = html.slice(heading.index + heading[0].length);
  const end = section.search(/<hr\b/i);
  if (end >= 0) section = section.slice(0, end);

  const items: FaqItem[] = [];
  for (const block of section.matchAll(/<(p|h[2-6]|ul|ol)\b[^>]*>([\s\S]*?)<\/\1>/gi)) {
    const text = plainText(block[2]);
    if (!text) continue;

    const question = text.match(QUESTION);
    if (question) {
      items.push({ question: question[1].trim(), answer: "" });
      continue;
    }
    const current = items.at(-1);
    if (current) current.answer = current.answer ? `${current.answer} ${text}` : text;
  }

  return items.filter((item) => item.question && item.answer);
}
